import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedCardTypeCarousel from '@/components/feature/card-type/VirtualizedCardTypeCarousel';
import { BuildingMenuType } from '@/enums';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styles from './CardTypePage.module.scss';
import { useCustomMenuContent } from '@/hooks/custom-menu';
import { useTranslation } from 'react-i18next';

const CardTypePage = () => {
    const { t } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const { isHorizontal } = useOrientation();
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const customContent = useCustomMenuContent(gnbMenuId);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        if (gnbMenuId) {
            return listMenus.find((menu) => menu.id === gnbMenuId && menu.menuType === BuildingMenuType.CUSTOM_MENU);
        }
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.CUSTOM_MENU);
    }, [server?.buildingMenus, ui.language, gnbMenuId]);

    const pageClassname = useMemo(() => {
        return clsx(styles['card-type'], {
            [styles['card-type__vertical']]: !isHorizontal,
            [styles['card-type__horizontal']]: isHorizontal,
            [styles['card-type__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['card-type__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['card-type__carousel'], {
            [styles['card-type__carousel__horizontal']]: isHorizontal,
            [styles['card-type__carousel__vertical']]: !isHorizontal,
            [styles['card-type__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['card-type__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['card-type__carousel__vertical__empty']]: customContent.length === 0 && !isHorizontal,
            [styles['card-type__carousel__vertical__empty__halfmode']]: customContent.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['card-type__carousel__horizontal__empty']]: customContent.length === 0 && isHorizontal,
            [styles['card-type__carousel__horizontal__empty__halfmode']]: customContent.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, customContent.length]);

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['card-type__header'], {
                    [styles['card-type__header--horizontal']]: isHorizontal,
                    [styles['card-type__header--vertical']]: !isHorizontal,
                    [styles['card-type__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader
                    title={(currentMenu?.name as string) || ''}
                    description={currentMenu?.description || ''}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>

            <div className={carouselClassname}>
                {customContent.length > 0 ? (
                    <VirtualizedCardTypeCarousel contents={customContent} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="card-type-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>
        </div>
    );
};

export default CardTypePage;
